import { useEffect, useMemo } from 'react';
import './Ticket.css';
import { useAuth } from '../../contexts/AuthContext';
import { useTicket } from '../../hooks/useTicket';
import { useParams } from 'react-router-dom';
// import AttachmentFiles from '../../composants/AttachmentFiles/AttachmentFiles';
import TicketChat from '../../composants/Ticket/TicketChat/TicketChat';
import { useTicketComments } from '../../hooks/useTicketComments';

type TicketRouteParams = { id?: string };

export default function TicketPage() {
  const { id: idParam } = useParams<TicketRouteParams>();
  const numericId = idParam ? Number(idParam) : NaN;
  const ticketId = Number.isFinite(numericId) ? numericId : null;

  useEffect(() => {
    console.log('Rendering TicketPage for ticket ID:', ticketId ?? idParam);
  }, [ticketId, idParam]);

  const { user } = useAuth();
  const { ticket, loading, error, reload: reloadTicket } = useTicket(ticketId);
  const {
    comments,
    loading: commentsLoading,
    error: commentsError,
    sending: sendingComment,
    sendError,
    sendComment,
  } = useTicketComments(ticketId);

  const currentUserId = useMemo(() => {
    if (user?.id == null) return undefined;
    const parsed = Number(user.id);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [user?.id]);

  const formattedContent = useMemo(() => {
    if (!ticket || typeof ticket.content !== 'string') return null;
    return ticket.content.split(/\r?\n/);
  }, [ticket]);

  const headerInfo = useMemo(() => {
    const status = ticket?.status;
    const statusLabel =
      typeof status === 'string'
        ? status
        : status?.label ?? status?.key ?? '—';

    return {
      subject: ticket?.subject ?? 'Ticket',
      ticketNumber: ticket?.ticket_no ?? ticketId,
      statusLabel,
      priority: ticket?.priority ?? '—',
      createdAt: ticket?.created_at
        ? new Date(ticket.created_at).toLocaleString('fr-FR')
        : '—',
      updatedAt: ticket?.updated_at
        ? new Date(ticket.updated_at).toLocaleString('fr-FR')
        : '—',
      office: ticket?.office?.name ?? '—',
      commentsCount: ticket?.comments_count ?? comments.length,
    };
  }, [ticket, ticketId, comments.length]);

  const handleSendMessage = async (message: string) => {
    if (currentUserId == null) {
      throw new Error("Utilisateur non authentifié.");
    }
    await sendComment(message, currentUserId);
  };

  if (ticketId === null) {
    return <p className="error-message">Identifiant de ticket invalide.</p>;
  }
  return (
    <div className="ticket-container">
      <div className="ticket-page">
      <header className="ticket-page__header ticket-page__header--single">
        <p className="ticket-page__breadcrumb">Support / Tickets / #{ticketId}</p>
        <h1>{headerInfo.subject}</h1>
        <p className="ticket-page__subtitle">Consultez toutes les informations et les échanges pour ce ticket.</p>
      </header>

      {loading && <p>Chargement du ticket...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading && !error && (
        <section className="ticket-layout">
          <div className="ticket-layout__column ticket-layout__column--info">
            <div className="ticket-card ticket-card--info">
              <h3>Informations</h3>
              <div className="ticket-details ticket-details--full">
                <div className="ticket-detail"><div className="label">Ticket n°</div><div className="value">{headerInfo.ticketNumber}</div></div>
                <div className="ticket-detail"><div className="label">Statut</div><div className="value"><span className="status-pill status-pill--accent">{headerInfo.statusLabel}</span></div></div>
                <div className="ticket-detail"><div className="label">Priorité</div><div className="value">{headerInfo.priority}</div></div>
                <div className="ticket-detail"><div className="label">Créé le</div><div className="value">{headerInfo.createdAt}</div></div>
                <div className="ticket-detail"><div className="label">Mise à jour</div><div className="value">{headerInfo.updatedAt}</div></div>
                <div className="ticket-detail"><div className="label">Bureau</div><div className="value">{headerInfo.office}</div></div>
                <div className="ticket-detail"><div className="label">Commentaires</div><div className="value">{headerInfo.commentsCount}</div></div>
                <div className="ticket-detail ticket-detail--description">
                  <div className="label">Description</div>
                  <div className="value value--multiline">
                    {formattedContent?.length
                      ? formattedContent.map((line: string, index: number) => (
                          <span key={index}>
                            {line}
                            {index < formattedContent.length - 1 && <br />}
                          </span>
                        ))
                      : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="ticket-card">
              <AttachmentFiles ticketId={ticketId} />
            </div> */}
          </div>

          <div className="ticket-layout__column ticket-layout__column--chat">
            <div className="ticket-card ticket-card--chat">
              <h3>Échanges</h3>
              {commentsError && <p className="error-message">{commentsError}</p>}
              {commentsLoading ? (
                <p>Chargement des échanges...</p>
              ) : (
                <TicketChat
                  comments={comments}
                  currentUserId={currentUserId}
                  onSend={handleSendMessage}
                  sending={sendingComment}
                  error={sendError}
                />
              )}
            </div>
          </div>
        </section>
      )}
      </div>
    </div>
  );
}